import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { addNotification } from '../store/slices/uiSlice';
import { clearTenantSettingsCache, clearClusterSettingsCache, reloadService, clearServiceCache, reloadAllServices, getServices, ServiceEntry } from '../api/configCacheApi';
import { ArrowPathIcon, ServerStackIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

type SvcState = 'idle' | 'loading' | 'ok' | 'error';

const CacheManagementPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [services,       setServices]       = useState<ServiceEntry[]>([]);
    const [loadingTenant,  setLoadingTenant]  = useState(false);
    const [loadingCluster, setLoadingCluster] = useState(false);
    const [lastTenant,     setLastTenant]     = useState<string | null>(null);
    const [lastCluster,    setLastCluster]    = useState<string | null>(null);
    const [svcStates,      setSvcStates]      = useState<Record<string, { state: SvcState; msg?: string }>>({});
    const [reloadingAll,   setReloadingAll]   = useState(false);

    useEffect(() => {
        getServices().then(r => {
            const list = r.data ?? [];
            setServices(list);
            setSvcStates(Object.fromEntries(list.map(s => [s.id, { state: 'idle' as SvcState }])));
        }).catch(() => {});
    }, []);

    const notify = (type: 'success' | 'error', message: string) =>
        dispatch(addNotification({ type, message }));

    const handleClearTenant = async () => {
        setLoadingTenant(true);
        try { await clearTenantSettingsCache(); setLastTenant(new Date().toLocaleTimeString()); notify('success', 'TenantSettings cache cleared.'); }
        catch (e: any) { notify('error', e?.message ?? 'Failed'); }
        finally { setLoadingTenant(false); }
    };

    const handleClearCluster = async () => {
        setLoadingCluster(true);
        try { await clearClusterSettingsCache(); setLastCluster(new Date().toLocaleTimeString()); notify('success', 'ClusterSettings cache cleared.'); }
        catch (e: any) { notify('error', e?.message ?? 'Failed'); }
        finally { setLoadingCluster(false); }
    };

    const setSvc = (id: string, state: SvcState, msg?: string) =>
        setSvcStates(prev => ({ ...prev, [id]: { state, msg } }));

    const handleSvcAction = useCallback(async (svcId: string, action: 'reload' | 'clear-tenant') => {
        setSvc(svcId, 'loading');
        try {
            if (action === 'reload') await reloadService(svcId);
            else await clearServiceCache(svcId);
            setSvc(svcId, 'ok');
            setTimeout(() => setSvc(svcId, 'idle'), 3000);
        } catch (e: any) {
            setSvc(svcId, 'error', e?.response?.data?.error ?? e?.message ?? 'Failed');
        }
    }, []);

    const handleReloadAll = async () => {
        setReloadingAll(true);
        try {
            const r = await reloadAllServices();
            const failed = Object.entries(r.results ?? {}).filter(([, v]: any) => !v.success).map(([k]) => k);
            if (failed.length === 0) notify('success', 'All services reloaded.');
            else notify('error', `Reload failed for: ${failed.join(', ')}`);
        } catch (e: any) { notify('error', e?.message ?? 'Failed'); }
        finally { setReloadingAll(false); }
    };

    return (
        <div className="space-y-6 px-6 py-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Cache Management</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Force-reload configuration caches from infrastructure services.
                </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-300">
                Clearing a cache triggers an immediate reload from MongoDB. All running services pick up fresh config on their next request.
            </div>

            {/* Global caches */}
            <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">Global caches</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        { title: 'Tenant Settings', desc: 'Tenant config — subdomain, database, Keycloak, security.', onClear: handleClearTenant, loading: loadingTenant, last: lastTenant },
                        { title: 'Cluster Settings', desc: 'MongoDB cluster config — connection, TLS, auth.', onClear: handleClearCluster, loading: loadingCluster, last: lastCluster },
                    ].map(c => (
                        <div key={c.title} className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 flex items-start justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <ServerStackIcon className="h-5 w-5 text-primary-500" />
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{c.title}</h3>
                                </div>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{c.desc}</p>
                                {c.last && <p className="mt-1 text-xs text-green-600 dark:text-green-400">Cleared at {c.last}</p>}
                            </div>
                            <button onClick={c.onClear} disabled={c.loading}
                                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                                <ArrowPathIcon className={`h-4 w-4 ${c.loading ? 'animate-spin' : ''}`} />
                                {c.loading ? 'Clearing…' : 'Clear'}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Per-service */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Per-service config reload</h2>
                    <button onClick={handleReloadAll} disabled={reloadingAll}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50">
                        <ArrowPathIcon className={`h-3.5 w-3.5 ${reloadingAll ? 'animate-spin' : ''}`} />
                        Reload all services
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {services.length === 0 && (
                        <p className="text-sm text-gray-400 dark:text-gray-500 col-span-3">
                            Loading services from tenant configurations…
                        </p>
                    )}
                    {services.map(svc => {
                        const st = svcStates[svc.id];
                        return (
                            <div key={svc.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{svc.label}</span>
                                    {st.state === 'loading' && <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />}
                                    {st.state === 'ok'      && <CheckCircleIcon className="h-4 w-4 text-green-500" />}
                                    {st.state === 'error'   && <ExclamationCircleIcon className="h-4 w-4 text-red-500" />}
                                </div>
                                {st.state === 'error' && st.msg && (
                                    <p className="text-xs text-red-500 dark:text-red-400 mb-2">{st.msg}</p>
                                )}
                                <div className="flex gap-2">
                                    <button onClick={() => handleSvcAction(svc.id, 'reload')} disabled={st.state === 'loading'}
                                        className="flex-1 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40">
                                        Reload config
                                    </button>
                                    <button onClick={() => handleSvcAction(svc.id, 'clear-tenant')} disabled={st.state === 'loading'}
                                        className="flex-1 text-xs px-2 py-1 rounded border border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 disabled:opacity-40">
                                        Clear tenant cache
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CacheManagementPage;
